<template>
  <section
    id="hero"
    class="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero pt-20"
  >
    <!-- Animated Background Elements -->
    <div class="absolute inset-0 overflow-hidden">
      <!-- Floating Shapes -->
      <div
        class="absolute top-1/4 left-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-float-blob-1"
      ></div>
      <div
        class="absolute top-1/3 right-1/4 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl animate-float-blob-2"
      ></div>
      <div
        class="absolute bottom-1/4 left-1/3 w-80 h-80 bg-accent-400/10 rounded-full blur-3xl animate-float-blob-3"
      ></div>

      <!-- Grid Pattern -->
      <div class="absolute inset-0 bg-grid-pattern opacity-5"></div>
    </div>

    <!-- Content -->
    <div class="container-custom relative z-10">
      <div class="max-w-4xl mx-auto text-center">
        <!-- Main Heading -->
        <h1
          class="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight"
        >
          Trusted Digital Solutions
          <span class="block mt-2 text-accent-200"> For Your Business </span>
        </h1>

        <!-- Subheading -->
        <p
          class="text-xl md:text-2xl text-gray-100 mb-8 max-w-2xl mx-auto"
        >
          Reliable digital solutions to grow your business and boost your online
          presence. Build with confidence with Devolysis.
        </p>

        <!-- CTA Buttons -->
        <div
          class="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            @click="scrollToSection('#portfolio')"
            class="btn-secondary group"
          >
            View Our Work
            <ArrowRight
              :size="20"
              class="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
          <button
            @click="scrollToSection('#contact')"
            class="btn-outline bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-primary"
          >
            Schedule a Consultation
          </button>
        </div>
      </div>
    </div>

    <!-- Scroll Indicator -->
    <transition name="fade">
      <div
        v-if="showScrollIndicator"
        class="fixed bottom-8 right-8 z-20"
      >
        <div class="flex flex-col items-center animate-bounce">
          <span class="text-white/70 text-sm mb-2">Scroll to explore</span>
          <ChevronDown :size="24" class="text-white/70" />
        </div>
      </div>
    </transition>

    <!-- Decorative Elements -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-white dark:to-dark-bg opacity-50"
      ></div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { ArrowRight, ChevronDown } from "lucide-vue-next";
import { useSmoothScroll } from "@/composables/useSmoothScroll";

const { scrollToSection } = useSmoothScroll();

const showScrollIndicator = ref(true);

const handleScroll = () => {
  showScrollIndicator.value = window.scrollY < 200;
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.bg-grid-pattern {
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
